import { handleResponse } from './helpers';
import { notifyUnauthorized } from './authEvents';
import { AUTH } from './endpoints';

const API_BASE =
  (
    process.env.REACT_APP_API_BASE_URL
  ).replace(/\/$/, '');

export class ApiError extends Error {
  constructor(message, status, code) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
  }
}

let accessToken = null;

export function setAccessToken(token) {
  accessToken = token;
}

export function getAccessToken() {
  return accessToken;
}

function isAuthLoginPath(path) {
  return (
    path === AUTH.LOGIN ||
    path.startsWith(`${AUTH.LOGIN}?`) ||
    path === AUTH.SIGNUP ||
    path.startsWith(`${AUTH.SIGNUP}?`)
  );
}

function isSessionExpiry401(path) {
  if (isAuthLoginPath(path)) return false;
  if (
    path === AUTH.CHANGE_PASSWORD ||
    path.startsWith(`${AUTH.CHANGE_PASSWORD}?`)
  ) {
    return false;
  }
  return true;
}

export async function apiRequest(path, options = {}) {
  const headers = new Headers(options.headers);
  if (!headers.has('Content-Type') && options.body) {
    headers.set('Content-Type', 'application/json');
  }
  if (accessToken) {
    headers.set('Authorization', `Bearer ${accessToken}`);
  }

  const res = await fetch(`${API_BASE}${path}`, {
    ...options,
    headers,
  });

  try {
    return await handleResponse(res);
  } catch (err) {
    const status = err?.status;
    const hadToken = Boolean(accessToken);
    if (status === 401 && hadToken && isSessionExpiry401(path)) {
      // Drop the token immediately so parallel requests don't keep retrying auth.
      accessToken = null;
      notifyUnauthorized('Please sign in — your session has expired.');
    } else if (
      status === 403 &&
      hadToken &&
      /deactivat/i.test(String(err?.message || ''))
    ) {
      accessToken = null;
      notifyUnauthorized(
        'This account is no longer active. Sign in with a different account.',
      );
    }
    if (err.name === 'ApiError' || status) {
      throw new ApiError(err.message, status, err.code);
    }
    throw err;
  }
}

export const api = {
  get: (path) => apiRequest(path),
  post: (path, body) =>
    apiRequest(path, {
      method: 'POST',
      body: body !== undefined ? JSON.stringify(body) : undefined,
    }),
  patch: (path, body) =>
    apiRequest(path, {
      method: 'PATCH',
      body: body !== undefined ? JSON.stringify(body) : undefined,
    }),
  delete: (path) => apiRequest(path, { method: 'DELETE' }),
};
